import React, { useState } from 'react';
import { useProjects } from '../context/ProjectContext';
import { createProject, ProjectSummary } from '../services/api';
import { notify, promptDialog } from './Notifications';
import { CloseIcon, FolderIcon, MoreIcon, PlusIcon } from './icons';

const DEFAULT_VISIBLE = 5;

interface Props {
  // Called after a project is opened, so the host can show its page in place of the current chat.
  onProjectOpened?: (projectId: string) => void;
}

const ProjectSidebar: React.FC<Props> = ({ onProjectOpened }) => {
  const { projects, activeProjectId, selectProject, refreshProjects } = useProjects();
  const [expanded, setExpanded] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const visible = expanded ? projects : projects.slice(0, DEFAULT_VISIBLE);
  const hasMore = projects.length > DEFAULT_VISIBLE;
  const activeProject = projects.find((p) => p.id === activeProjectId);

  const open = async (projectId: string) => {
    await selectProject(projectId);
    onProjectOpened?.(projectId);
  };

  const handleCreate = async () => {
    if (isCreating) return;
    const name = await promptDialog('Name your new project', { placeholder: 'e.g. Q4 pricing review', confirmLabel: 'Create' });
    if (!name) return;
    try {
      setIsCreating(true);
      const project = await createProject({ name });
      await refreshProjects();
      await open(project.id);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      notify(typeof detail === 'string' ? detail : 'Could not create the project.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="cv-projects">
      <div className="cv-projects__label">Projects</div>

      <button type="button" className="cv-projects__row" onClick={handleCreate} disabled={isCreating}>
        <PlusIcon size={16} />
        <span>New project</span>
      </button>

      {visible.map((p: ProjectSummary) => (
        <button
          type="button"
          key={p.id}
          className={`cv-projects__row${activeProjectId === p.id ? ' is-active' : ''}`}
          onClick={() => open(p.id)}
          aria-current={activeProjectId === p.id ? 'page' : undefined}
          title={`Open ${p.name}`}
        >
          <span style={{ display: 'flex', color: p.color || undefined }}><FolderIcon size={16} /></span>
          <span className="cv-projects__name">{p.name}</span>
        </button>
      ))}

      {hasMore && (
        <button type="button" className="cv-projects__row cv-projects__row--muted" onClick={() => setExpanded((v) => !v)}>
          <MoreIcon size={16} />
          <span>{expanded ? 'Show fewer' : `Show all ${projects.length}`}</span>
        </button>
      )}

      {activeProject && (
        <div className="cv-projects__active">
          <span>Showing chats in <strong>{activeProject.name}</strong></span>
          <button
            type="button"
            className="cv-icon-btn"
            onClick={() => selectProject(null)}
            data-tooltip="Close project"
            aria-label="Close project"
          >
            <CloseIcon size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ProjectSidebar;
